import React from 'react';
import { cva } from 'class-variance-authority';
import cn from '../utils/cn';

// Not value/onValueChange (deliberate deviation from the Root+Item rule):
// pills host a nested remove button — invalid inside a Radix ToggleGroup.Item
// button — and the "add" pill is a selected state on some screens and a push
// action on others, so selection stays at the call site via `active`.

// h-9 fixed so plain, removable and "add" pills all line up; icons are
// normalized to size-4 regardless of the size prop the icon receives.
const pillVariants = cva(
  'inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md font-medium text-sm px-3.5 border border-solid transition-colors [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:pointer-events-none',
  {
    // Colours live in the compounds only: cn() joins without merging, so a
    // disabled class could not override an active/inactive colour reliably.
    variants: {
      active: { true: '', false: '' },
      disabled: {
        true: 'shadow-none cursor-not-allowed',
        false: 'shadow-sm cursor-pointer',
      },
    },
    compoundVariants: [
      {
        active: true,
        disabled: false,
        class: 'bg-brand/10 border-brand text-brand hover:bg-brand/20',
      },
      {
        active: false,
        disabled: false,
        class:
          'bg-white border-input text-gray-500 hover:border-gray-300 hover:text-gray-700',
      },
      // Locked but still showing what is selected (read-only editors).
      {
        active: true,
        disabled: true,
        class: 'bg-brand/5 border-brand/30 text-brand/60',
      },
      {
        active: false,
        disabled: true,
        class: 'bg-gray-100 border-gray-100 text-gray-400',
      },
    ],
    defaultVariants: { active: false, disabled: false },
  },
);

const innerButton =
  'border-none bg-transparent p-0 text-sm font-medium text-inherit inline-flex items-center gap-1.5 rounded-md cursor-pointer disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

type KoobPillProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'className'
> & {
  active?: boolean;
  /** Renders a trash button inside the pill for deleting the item it represents. */
  onRemove?: React.MouseEventHandler<HTMLButtonElement>;
};

const KoobPill = React.forwardRef<HTMLButtonElement, KoobPillProps>(
  (
    {
      active = false,
      onRemove,
      disabled = false,
      type = 'button',
      children,
      ...props
    },
    ref,
  ) => {
    if (!onRemove) {
      return (
        <button
          ref={ref}
          type={type}
          disabled={disabled}
          className={cn(
            pillVariants({ active, disabled }),
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          )}
          {...props}
        >
          {children}
        </button>
      );
    }

    // With a remove control the pill can't be a single <button> (no nested
    // interactive content), so the pill chrome moves to a span holding two
    // plain buttons.
    return (
      <span className={pillVariants({ active, disabled })}>
        <button
          ref={ref}
          type={type}
          disabled={disabled}
          className={innerButton}
          {...props}
        >
          {children}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={onRemove}
          aria-label="Remove"
          className={cn(
            innerButton,
            '-mr-2 size-6 justify-center transition-colors hover:bg-red-500/10 hover:text-red-500 disabled:hover:bg-transparent disabled:hover:text-inherit',
          )}
        >
          <i className="fa-light fa-trash" />
        </button>
      </span>
    );
  },
);
KoobPill.displayName = 'KoobPill';

export { KoobPill };
